import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { processAssessmentResults } from '../utils/scoring';
import { generateStage1Questions, generateStage2Questions } from '../utils/questionFactory';
import { isDevToolsEnabled } from '../utils/devMode';

export const ASSESSMENT_STAGES = {
  REGISTRATION: 'registration',
  INTRODUCTION: 'introduction',
  QUESTIONS: 'questions',
  PROCESSING: 'processing',
  PAYMENT_WALL: 'payment_wall',
  RESULTS: 'results'
};

export const USER_TIERS = {
  FREE: 'free',
  ENHANCED: 'enhanced', 
  PREMIUM: 'premium'
};

export const TIER_PRICING = {
  [USER_TIERS.ENHANCED]: 24.99,
  [USER_TIERS.PREMIUM]: 39.99
};

const AssessmentContext = createContext();

export const AssessmentProvider = ({ children }) => {
  const navigate = useNavigate();

  const [userData, setUserData] = useState({
    name: '',
    email: '',
    age: '',
    currentRole: '',
    lookingForWork: false,
    coachCode: '',
    coachId: null
  });
  


  const [stage, setStage] = useState(ASSESSMENT_STAGES.REGISTRATION);
  const [responses, setResponses] = useState({});
  const [results, setResults] = useState(null);
  const [progress, setProgress] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  const [userTier, setUserTier] = useState(USER_TIERS.FREE);
  const [showUpgradePrompts, setShowUpgradePrompts] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeTarget, setUpgradeTarget] = useState(null);

  // Engine state: stage1 then stage2
  const [enginePhase, setEnginePhase] = useState(1); // 1 or 2
  const [assessmentQuestions, setAssessmentQuestions] = useState([]);
  const [topElementIds, setTopElementIds] = useState([]);

  // devTestMode (the mock-checkout / dev path) is a dev tool: never on for real users.
  const [devTestMode, setDevTestMode] = useState(() => isDevToolsEnabled() && localStorage.getItem('devTestMode') === 'true');

  const toggleDevTestMode = () => {
    setDevTestMode(prev => {
      const next = !prev;
      localStorage.setItem('devTestMode', String(next));
      return next;
    });
  };

  useEffect(() => {
    // Initialize Stage 1 questions from authored bank
    const s1 = generateStage1Questions(42);
    setAssessmentQuestions(s1);
    setEnginePhase(1);
    setCurrentQuestionIndex(0);
  }, [userTier]);

  useEffect(() => {
    try {
      const savedResponses = localStorage.getItem('assessmentResponses');
      if (savedResponses) setResponses(JSON.parse(savedResponses));
      
      // Load legacy assessment results
      const savedResults = localStorage.getItem('assessmentResults');
      if (savedResults) setResults(JSON.parse(savedResults));
      
      // Load legacy user data
      const savedUserData = localStorage.getItem('userData');
      if (savedUserData) setUserData(JSON.parse(savedUserData));
      
      const savedTier = localStorage.getItem('userTier');
      if (savedTier && Object.values(USER_TIERS).includes(savedTier)) setUserTier(savedTier);
      const upgradePromptsPreference = localStorage.getItem('showUpgradePrompts');
      if (upgradePromptsPreference !== null) setShowUpgradePrompts(upgradePromptsPreference === 'true');
    } catch (error) {
      console.error('Error loading saved data:', error);
    }
  }, []);

  useEffect(() => {
    if (Object.keys(responses).length > 0) {
      localStorage.setItem('assessmentResponses', JSON.stringify(responses));
    }
  }, [responses]);

  useEffect(() => {
    if (userData.name || userData.email) {
      localStorage.setItem('userData', JSON.stringify(userData));
    }
  }, [userData]);

  useEffect(() => {
    localStorage.setItem('userTier', userTier);
  }, [userTier]);

  useEffect(() => {
    localStorage.setItem('showUpgradePrompts', showUpgradePrompts.toString());
  }, [showUpgradePrompts]);

  useEffect(() => {
    if (assessmentQuestions.length > 0) {
      const newProgress = ((currentQuestionIndex + 1) / assessmentQuestions.length) * 100;
      setProgress(newProgress);
    }
  }, [currentQuestionIndex, assessmentQuestions.length]);

  const registerUser = (userInfo) => {
    setUserData({ ...userData, ...userInfo });
    setStage(ASSESSMENT_STAGES.INTRODUCTION);
  };

  const updateCoachInfo = (coachCode, coachId) => {
    setUserData(prev => ({
      ...prev,
      coachCode,
      coachId
    }));
  };

  const startAssessment = () => {
    setStage(ASSESSMENT_STAGES.QUESTIONS);
    setCurrentQuestionIndex(0);
    navigate('/assessment');
  };

  const resetAssessment = () => {
    setResponses({});
    setCurrentQuestionIndex(0);
    localStorage.removeItem('assessmentResponses');
    localStorage.removeItem('assessmentResults');
    setStage(ASSESSMENT_STAGES.INTRODUCTION);
    navigate('/assessment');
  };

  const handleResponse = (response) => {
    const currentQuestion = assessmentQuestions[currentQuestionIndex];
    setResponses(prev => ({ ...prev, [currentQuestion.id]: response }));
  };

  function shouldStopStage2(top8Ids) {
    const perElementCounts = {};
    top8Ids.forEach(id => { perElementCounts[id] = 0; });
    Object.entries(responses).forEach(([qid]) => {
      if (qid.startsWith('s2_')) {
        // s2_id... format: use value elementId when present
        // Count per selected element if available in response
        const selected = responses[qid];
        if (typeof selected === 'string' && perElementCounts[selected] !== undefined) {
          perElementCounts[selected] += 1;
        }
      }
    });
    const allHaveThree = Object.values(perElementCounts).every(c => c >= 3);
    return allHaveThree;
  }

  const nextQuestion = () => {
    if (currentQuestionIndex < assessmentQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      return;
    }
    if (enginePhase === 1) {
      const partial = processAssessmentResults(responses, assessmentQuestions);
      const top8 = partial.topPreferences.slice(0, 8);
      setTopElementIds(top8);
      const s2 = generateStage2Questions(top8, 4);
      setAssessmentQuestions(s2);
      setEnginePhase(2);
      setCurrentQuestionIndex(0);
      return;
    }
    if (enginePhase === 2) {
      if (shouldStopStage2(topElementIds)) {
        submitAssessment();
      } else {
        setCurrentQuestionIndex(0);
      }
    }
  };

  const previousQuestion = () => {
    if (currentQuestionIndex > 0) setCurrentQuestionIndex(currentQuestionIndex - 1);
  };

  const getVisibleElements = (topPreferences = []) => {
    if (userTier === USER_TIERS.FREE) return topPreferences.slice(0, 3);
    if (userTier === USER_TIERS.ENHANCED) return topPreferences.slice(0, 6);
    return topPreferences;
  };

  const getElementScoreDisplay = (element, index) => {
    // Safety check for element
    if (!element || typeof element.score === 'undefined') {
      console.warn('getElementScoreDisplay: Invalid element or missing score', element, index);
      return 'N/A';
    }
    
    if (userTier === USER_TIERS.PREMIUM) return element.score;
    if (userTier === USER_TIERS.ENHANCED && index < 6) return element.score;
    if (userTier === USER_TIERS.ENHANCED && index >= 6) {
      return { type: 'upgrade_bubble', text: 'Upgrade to Premium to view your score', targetTier: USER_TIERS.PREMIUM };
    }
    if (userTier === USER_TIERS.FREE && index < 3) return element.score;
    const targetTier = userTier === USER_TIERS.FREE ? USER_TIERS.ENHANCED : USER_TIERS.PREMIUM;
    return { type: 'upgrade_bubble', text: `Upgrade to ${targetTier === USER_TIERS.ENHANCED ? 'Enhanced' : 'Premium'} to see your score`, targetTier };
  };

  const shouldShowUpgradePrompt = () => (userTier === USER_TIERS.FREE || userTier === USER_TIERS.ENHANCED) && showUpgradePrompts;

  const showUpgradeModalForTier = (targetTier) => {
    setUpgradeTarget(targetTier);
    setShowUpgradeModal(true);
  };

  const submitAssessment = () => {
    setStage(ASSESSMENT_STAGES.PROCESSING);
    setTimeout(() => { processResults(); }, 800);
  };

  const processResults = () => {
    try {
      const processedResults = processAssessmentResults(responses, assessmentQuestions, userTier);
      localStorage.setItem('assessmentResults', JSON.stringify(processedResults));
      setResults(processedResults);
      setStage(ASSESSMENT_STAGES.RESULTS);
      
      // Navigate to legacy results view
      navigate('/results');
    } catch (error) {
      console.error('Error processing results:', error);
    }
  };

  // Explicitly move flow to registration stage (used by /register route)
  const beginRegistration = () => {
    setStage(ASSESSMENT_STAGES.REGISTRATION);
    setCurrentQuestionIndex(0);
    navigate('/assessment');
  };

  const handleTierUpgrade = (newTier) => {
    setUserTier(newTier);
    localStorage.setItem('userTier', newTier);
    localStorage.setItem('tierUpgradeDate', new Date().toISOString());
    setShowUpgradeModal(false);
    
    // For legacy flow, reprocess results if available
    if (results) {
      const reprocessedResults = processAssessmentResults(responses, assessmentQuestions, newTier);
      localStorage.setItem('assessmentResults', JSON.stringify(reprocessedResults));
      setResults(reprocessedResults);
    }
  };

  const cancelUpgrade = () => { setShowUpgradeModal(false); setUpgradeTarget(null); };
  const getCurrentQuestion = () => assessmentQuestions[currentQuestionIndex] || null;

  const getTierFeatures = () => {
    const features = {
      [USER_TIERS.FREE]: { maxElements: 3, domainAnalysis: true, constellationView: true, elementGrid: true, careerRecommendations: 'basic', pdfReport: false, assessmentStages: 1, description: 'Domain Discovery - 8 minutes' },
      [USER_TIERS.ENHANCED]: { maxElements: 6, domainAnalysis: true, constellationView: true, elementGrid: true, careerRecommendations: 'enhanced', pdfReport: false, assessmentStages: 2, description: 'Element Focus - 20 minutes' },
      [USER_TIERS.PREMIUM]: { maxElements: 51, domainAnalysis: true, constellationView: true, elementGrid: true, careerRecommendations: 'complete', pdfReport: true, assessmentStages: 3, industryAddon: true, description: 'Complete Profile - 30 minutes' }
    };
    return features[userTier];
  };

  const contextValue = {
    stage, userData, responses, results, progress, currentQuestionIndex,
    userTier, showUpgradePrompts, showUpgradeModal, upgradeTarget,
    devTestMode, toggleDevTestMode,
    registerUser, startAssessment, resetAssessment, beginRegistration, handleResponse, nextQuestion, previousQuestion, submitAssessment,
    getCurrentQuestion, getVisibleElements, getElementScoreDisplay, shouldShowUpgradePrompt, showUpgradeModalForTier, handleTierUpgrade, cancelUpgrade, getTierFeatures, updateCoachInfo,
    USER_TIERS, TIER_PRICING, ASSESSMENT_STAGES
  };

  return (
    <AssessmentContext.Provider value={contextValue}>
      {children}
    </AssessmentContext.Provider>
  );
};

export const useAssessment = () => {
  const context = useContext(AssessmentContext);
  if (!context) throw new Error('useAssessment must be used within an AssessmentProvider');
  return context;
};

export default AssessmentContext; 
